import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  subscriptionServices,
  type Invoice,
  type Subscription
} from '../../lib/supabase';
import { fixedInvoiceServices } from '../../lib/fixed-invoice-payment-services';
import { formatCurrency, formatInvoiceNumber } from '../../lib/formatters';
import { PlusIcon } from 'lucide-react';

const InvoicesTab = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const { id: customerId } = useParams<{ id: string }>();

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!customerId) {
        setError('No customer ID provided');
        setLoading(false);
        return;
      }

      try {
        // First get customer's subscriptions
        const { data: subscriptionsData, error: subsError } = await subscriptionServices.getByCustomer(customerId);
        if (subsError) throw subsError;
        setSubscriptions(subscriptionsData || []);
        
        if (!subscriptionsData?.length) {
          setInvoices([]);
          return;
        }

        // Get invoices for all subscriptions using Promise.all to fetch them in parallel
        const invoicePromises = subscriptionsData.map(sub => 
          fixedInvoiceServices.getBySubscription(sub.id!)
        );
        
        const results = await Promise.all(invoicePromises);
        const allInvoices = results
          .filter(result => !result.error && result.data)
          .map(result => result.data)
          .flat()
          .filter((invoice): invoice is Invoice => invoice !== null);
        
        setInvoices(allInvoices);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch invoices');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [customerId]);

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'failed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) return <div className="text-center p-4">Loading...</div>;
  if (error) return <div className="text-red-600 p-4">{error}</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-medium leading-6 text-gray-900">Invoices</h3>
          <p className="mt-1 text-sm text-gray-500">
            Manage customer invoices and payments
          </p>
        </div>
        <Link to={`/invoices/create`} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Invoice
        </Link>
      </div>

      {invoices.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          No invoices found for this customer.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Paid Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <Link 
                      to={`/invoices/${invoice.id}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {formatInvoiceNumber(invoice)}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatCurrency(invoice.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(invoice.due_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {invoice.paid_at ? formatDate(invoice.paid_at) : 'Unpaid'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoicesTab;
